import { HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { Observable, finalize, share } from 'rxjs';

/**
 * Clique duplo não vira dois cadastros. Enquanto um POST/PUT/PATCH/DELETE está em voo, outro
 * IDÊNTICO (mesmo método, URL e corpo) não sai: pega carona na mesma chamada e recebe a mesma
 * resposta.
 *
 * Por que aqui e não só no botão: todo form já desabilita o Salvar com `isLoading`, mas isso
 * depende de CADA tela ligar a flag antes da chamada, em TODOS os gatilhos (cabeçalho, lateral,
 * rodapé das abas, Enter do form), e o `[disabled]` só chega no DOM depois da detecção de mudanças.
 * Uma tela que esquece qualquer um desses vira cadastro duplicado — foi assim que a mesma empresa
 * entrou duas vezes. A guarda no HTTP vale pra todas as telas, inclusive as que ainda não existem.
 *
 * Compartilhar (e não descartar) o segundo envio: quem clicou duas vezes ainda espera UMA resposta
 * em cada assinatura — descartar deixaria um spinner girando pra sempre.
 *
 * GET fica de fora: leitura repetida é inofensiva e várias telas pedem o mesmo recurso de propósito.
 */
const inFlight = new Map<string, Observable<HttpEvent<unknown>>>();

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export const duplicateSubmitInterceptor: HttpInterceptorFn = (req, next) => {
  if (!MUTATING.has(req.method)) return next(req);

  const key = requestKey(req);
  // Corpo que não dá pra comparar (arquivo, FormData): não arrisca juntar envios diferentes.
  if (key === null) return next(req);

  const running = inFlight.get(key);
  if (running) return running;

  const shared = next(req).pipe(
    finalize(() => inFlight.delete(key)),
    share(),
  );
  inFlight.set(key, shared);
  return shared;
};

function requestKey(req: HttpRequest<unknown>): string | null {
  // A empresa ativa entra na chave: o mesmo corpo pra empresas diferentes são envios diferentes.
  const target = `${req.method} ${req.urlWithParams} @${req.headers.get('X-Company-Id') ?? ''}`;

  const body = req.body;
  if (body === null || body === undefined) return target;
  if (typeof body === 'string') return `${target} ${body}`;
  if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) return null;

  try {
    return `${target} ${JSON.stringify(body)}`;
  } catch {
    return null;
  }
}

/** Só pra teste: o mapa é de módulo e sobreviveria entre um spec e outro. */
export function resetDuplicateSubmitGuard(): void {
  inFlight.clear();
}
